// Story 10.3 — tipos compartidos entre client/server (mismo shape que el
// mapper en `apps/api/src/conversations/mapper.ts`). Evita import del backend
// desde el frontend.

export type MessageDirection = 'inbound' | 'outbound';

export type MessageType =
  | 'text'
  | 'interactive_buttons'
  | 'interactive_list'
  | 'media'
  | 'location'
  | 'contact'
  | 'template'
  | 'unknown';

export interface InteractiveButtons {
  buttons: Array<{ id: string; title: string }>;
}

export interface InteractiveListSection {
  title?: string;
  rows: Array<{ id: string; title: string; description?: string }>;
}

export interface InteractiveList {
  sections: InteractiveListSection[];
  buttonText?: string;
}

export interface ConversationMessage {
  kind: 'message';
  id: string;
  eventId: number;
  direction: MessageDirection;
  timestamp: string;
  type: MessageType;
  text?: string;
  interactive?: { buttons?: InteractiveButtons; list?: InteractiveList };
  mediaUrl?: string;
  mediaCaption?: string;
  replyInputId?: string;
  /** Story 12.6 — `true` para mensajes outbound de tipo `text` que el engine
   *  degradó desde un interactive original (provider Evolution Go).
   *  Renderizamos un badge `[Modo texto]` en MessageBubble para distinguirlos. */
  degraded?: boolean;
}

export interface FlowEvent {
  kind: 'flow';
  id: string;
  eventId: number;
  eventName: 'flow:step-executed' | 'flow:error' | 'flow:completed';
  timestamp: string;
  flowId?: string;
  stepId?: string;
  stepIndex?: number;
  errorMessage?: string;
}

export interface ConversationSummary {
  sessionId: number;
  userPhone: string;
  userName: string | null;
  status: string;
  flowId: string | null;
  stepIndex: number | null;
  lastMessageAt: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

export interface ConversationListResponse {
  data: ConversationSummary[];
  pagination: { total: number; limit: number; offset: number };
  partial: boolean;
}

export interface ConversationSessionDto {
  id: number;
  botId: string;
  userPhone: string;
  userName: string | null;
  sessionData: Record<string, unknown>;
  flowId: string | null;
  stepIndex: number | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

export interface ConversationDetail {
  session: ConversationSessionDto;
  messages: ConversationMessage[];
  flowEvents: FlowEvent[];
  partial: boolean;
}

/** Enmascara `+5491155551234` → `+54***1234`. */
export function maskPhone(phone: string): string {
  if (!phone) return phone;
  const digits = phone.replace(/[^\d+]/g, '');
  if (digits.length <= 6) return digits;
  const head = digits.slice(0, 3);
  const tail = digits.slice(-4);
  return `${head}***${tail}`;
}

/**
 * Para un mensaje inbound con `replyInputId`, busca el último outbound
 * interactive previo y retorna el id del button/row que coincide. Útil para
 * resaltar la opción seleccionada visualmente.
 */
/** P-278: en `ConversationDetail` esto se llama una vez por cada mensaje
 *  inbound — sin cota, una conversación de N mensajes era O(N²) en el
 *  peor caso (~500k ops para 1000 mensajes). Limitamos el walk-back a
 *  los últimos `MAX_REPLY_LOOKBACK` mensajes; en práctica el reply es
 *  inmediato (≤2 mensajes atrás), así que la cota no quita correctness.
 */
const MAX_REPLY_LOOKBACK = 30;

export function findReplySelection(
  messages: ConversationMessage[],
  inboundIndex: number,
): { sourceMessageId: string; selectedId: string } | null {
  const reply = messages[inboundIndex];
  if (!reply || reply.direction !== 'inbound' || !reply.replyInputId) return null;
  const stopAt = Math.max(0, inboundIndex - MAX_REPLY_LOOKBACK);
  for (let i = inboundIndex - 1; i >= stopAt; i--) {
    const candidate = messages[i];
    if (candidate.direction !== 'outbound' || !candidate.interactive) continue;
    const buttonIds = candidate.interactive.buttons?.buttons.map((b) => b.id) ?? [];
    const rowIds =
      candidate.interactive.list?.sections.flatMap((s) => s.rows.map((r) => r.id)) ?? [];
    if (buttonIds.includes(reply.replyInputId) || rowIds.includes(reply.replyInputId)) {
      return { sourceMessageId: candidate.id, selectedId: reply.replyInputId };
    }
  }
  return null;
}

export interface ConversationDisplayName {
  primary: string;
  secondary?: string;
}

export function deriveDisplayName(summary: {
  userName: string | null;
  userPhone: string;
}): ConversationDisplayName {
  if (summary.userName && summary.userName.trim().length > 0) {
    return { primary: summary.userName, secondary: maskPhone(summary.userPhone) };
  }
  return { primary: maskPhone(summary.userPhone) };
}
